import { Injectable, inject } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import { firstValueFrom, filter, take } from 'rxjs';
import { UserService } from '../user/user.service';
import { TankService } from '../tank/tank.service';
import { AddTankModalComponent } from '../../components/add-tank-modal/add-tank-modal.component';

/**
 * Service to manage onboarding flows for new users
 */
@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private userService = inject(UserService);
  private tankService = inject(TankService);
  private modalController = inject(ModalController);
  private alertController = inject(AlertController);

  private isCheckingOnboarding = false;

  /**
   * Check and trigger first-time premium user onboarding if conditions are met:
   * 1. User has premium access (subscription or grandfathered)
   * 2. User has not seen the premium onboarding before
   * 3. User has zero tanks configured
   */
  async checkAndTriggerPremiumOnboarding(): Promise<boolean> {
    // Prevent multiple simultaneous checks
    if (this.isCheckingOnboarding) {
      return false;
    }

    this.isCheckingOnboarding = true;

    try {
      // Wait for user profile to be loaded
      const profile = await firstValueFrom(
        this.userService.userProfile$.pipe(
          filter(p => p !== null),
          take(1)
        )
      );

      if (!profile) {
        return false;
      }

      // Check if user has premium access
      const hasPremium = this.userService.checkPremiumAccess();
      if (!hasPremium) {
        return false;
      }

      // Check if user has already seen the onboarding
      if (this.userService.hasSeenPremiumOnboarding()) {
        return false;
      }

      // Check if user has any tanks
      const userId = this.userService.userId;
      if (!userId) {
        return false;
      }

      const tanks = await firstValueFrom(
        this.tankService.getTanks(userId).pipe(take(1))
      );

      // If user has tanks, mark onboarding as complete and skip
      if (tanks && tanks.length > 0) {
        await this.userService.markPremiumOnboardingComplete();
        return false;
      }

      // All conditions met - show the onboarding flow
      await this.showPremiumOnboardingFlow();
      return true;
    } finally {
      this.isCheckingOnboarding = false;
    }
  }

  /**
   * Display the premium onboarding welcome message and then open the add tank modal
   */
  private async showPremiumOnboardingFlow(): Promise<void> {
    // Show welcome alert first
    const alert = await this.alertController.create({
      header: '🎉 Welcome to Reef Tracker Pro!',
      message: `Congratulations on your premium access! You now have full access to all reef tracking features.

To get started, let's set up your first tank. This will allow you to:
• Track water parameters
• Monitor your corals
• View your reef timeline
• And much more!`,
      buttons: [
        {
          text: 'Set Up My Tank',
          role: 'confirm',
          handler: () => {
            // Will open add tank modal after alert dismisses
          }
        }
      ],
      backdropDismiss: false,
    });

    await alert.present();
    await alert.onDidDismiss();

    // Now open the add tank modal
    await this.openAddTankModal();
  }

  /**
   * Open the add tank modal programmatically
   */
  private async openAddTankModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: AddTankModalComponent,
      componentProps: {
        isOnboarding: true,
      }
    });

    await modal.present();

    const { data, role } = await modal.onDidDismiss();

    // Mark onboarding as complete regardless of whether they added a tank
    // This ensures we don't keep showing the onboarding on every login
    await this.userService.markPremiumOnboardingComplete();
  }
}

